import Constants from '@/Constants';
import Item from '@/models/Item';
import { EvaluationLesson } from '@/models/Lesson';
import { LessonEnrollment } from '@/models/LessonEnrollment';
import { courseService } from '@/services/CourseService';
import { ApplicationState } from '@/store';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import CardItemThumb from '../CardItemThumb';
import './style.scss';

interface Props {
    lessonEnrollment: LessonEnrollment;
    lessonStatus: string;
    setCanShowContentLink: (value: boolean) => void;
}

const LMSContentCard: React.FC<Props> = ({ lessonEnrollment, lessonStatus, setCanShowContentLink }: Props) => {
    const { strings } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
    }));

    const getColor = () => {
        if (lessonStatus === strings.BLOCKED) {
            return 'disabled';
        }
        switch (lessonEnrollment.status) {
            case Constants.lessonEnrollmentStatus.INCOMPLETE:
            case Constants.lessonEnrollmentStatus.NOT_ATTEMPTED:
                return 'not-started';
            case Constants.lessonEnrollmentStatus.FAILED:
            case Constants.lessonEnrollmentStatus.PASSED:
            case Constants.lessonEnrollmentStatus.COMPLETED:
                return 'completed';
            default:
                return 'blocked';
        }
    };

    const [content, setContent] = useState<Item>();
    const [label, setLabel] = useState<string>(strings.LESSON);
    const [workload, setWorkload] = useState<string>('');
    const [status, setStatus] = useState<string>('');
    const [timeLimit, setTimeLimit] = useState<number>(0);
    const [score, setScore] = useState<number | null>(null);
    const [color, setColor] = useState(getColor());

    useEffect(() => {
        setContent(lessonEnrollment.lesson.content);
    }, [lessonEnrollment]);

    useEffect(() => {
        if (content) {
            setStatus(Constants.lessonEnrollmentStatus.INCOMPLETE !== lessonEnrollment.status ? lessonStatus.toUpperCase() : '');

            if (content.type === 'EvaluationContent') {
                configEvaluationValues();
            } else {
                setLabel(strings.LESSON);
                setWorkload(`${content.workload} ${strings.MINUTES}`);
                setCanShowContentLink(true);
            }
        }
    }, [content]);

    const configEvaluationValues = async () => {
        const lesson = lessonEnrollment.lesson as EvaluationLesson;

        setTimeLimit(lesson.timeLimit ? lesson.timeLimit : 0);
        setWorkload(`${lesson.questionsAmount} ${lesson.questionsAmount === 1 ? strings.QUESTION.toLowerCase() : strings.QUESTIONS.toLowerCase()}`);
        setLabel(strings.EVALUATION);

        if (lessonEnrollment.status !== Constants.lessonEnrollmentStatus.NOT_ATTEMPTED) {
            setScore(lessonEnrollment.grade);
            if (
                lessonEnrollment.status === Constants.lessonEnrollmentStatus.PASSED ||
                (lessonEnrollment.status === Constants.lessonEnrollmentStatus.COMPLETED && lessonEnrollment.grade >= lesson.masteryScore)
            ) {
                setStatus(strings.COURSE_PASSED.toUpperCase());
                setColor('PASSED');
            } else if (
                lessonEnrollment.status === Constants.lessonEnrollmentStatus.FAILED ||
                (lessonEnrollment.status === Constants.lessonEnrollmentStatus.COMPLETED && lessonEnrollment.grade < lesson.masteryScore)
            ) {
                setStatus(strings.COURSE_FAILED.toUpperCase());
                setColor('FAILED');
            } else {
                setStatus(`${strings.ATTEMPTS.toUpperCase()}: ${lessonEnrollment.attempts > 0 ? lessonEnrollment.attempts : 1}/${lesson.maxTries}`);
            }
        }
        setCanShowContentLink(true);
    };

    return (
        <div className="main">
            <div className={`card-item ${color}`}>
                <div className="card-item__body">
                    <div className="card-item__label">{label}</div>
                    <h3 className="card-item__title" title={content && content.name}>
                        {content && content.name}
                    </h3>
                    <p className="card-item__text" title={content && content.description}>
                        {content && content.description}
                    </p>
                    <div className="card-item__author"> {content && content.author ? content.author : '-'}</div>
                </div>
                {content && (
                    <CardItemThumb
                        imageSrc={content.mainImage}
                        isFinished={
                            lessonEnrollment.status === Constants.lessonEnrollmentStatus.PASSED ||
                            lessonEnrollment.status === Constants.lessonEnrollmentStatus.COMPLETED
                        }
                        isReproved={lessonEnrollment.status === Constants.lessonEnrollmentStatus.FAILED}
                    />
                )}
                <div className="card-item__footer">
                    <div className="card-item__duration">
                        {workload}
                        {Constants.lessonEnrollmentStatus.INCOMPLETE === lessonEnrollment.status &&
                            (!content || content.type !== 'EvaluationContent') && (
                                <div className="card-item__progress-bar-container">
                                    <div className="progress-bar"></div>
                                </div>
                            )}
                        <div className={color}>{status}</div>
                        {score !== null && lessonStatus !== strings.BLOCKED && (
                            <div className="color-primary">
                                {strings.SCORE.toUpperCase()}: {score}
                            </div>
                        )}
                    </div>
                    {timeLimit > 0 && (
                        <div className="card-item__timelimit">
                            <i className="material-icons">{Constants.icons.ALARM}</i>
                            <span>{courseService.formatWorkload(timeLimit, strings, true)}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LMSContentCard;
